import type { EntityType } from '#types/types';
import { ENTITY_TYPES } from '#types/types';

export type PortalUrlState = {
  chatSessionId: string | null;
  focusTarget: {
    entityType: EntityType;
    entityId: string;
    forceNewSession: boolean;
    tab: string | null;
  } | null;
};

const ENTITY_TYPE_SET = new Set<string>(ENTITY_TYPES);
// No Property entry: an asset has no page, so it has no tabs for a URL to deep-link into. A
// `?fx=Property:<id>&tab=UNITS` link from before now normalizes the tab away and focuses the asset
// without one, which is what every other tabless entity already does.
const PORTAL_FOCUS_TAB_MAP: Partial<Record<EntityType, readonly string[]>> = {
  Floor: ['FOUNDATION', 'UNITS', 'THREE_D', 'MAPPER'],
  Unit: ['OVERVIEW', 'LEADS', 'DEALS'],
  Portfolio: ['FOUNDATION', 'PROPERTIES', 'PORTFOLIO_FIT_CAMPAIGNS', 'PORTFOLIO_FITS'],
  Brand: ['OVERVIEW', 'LEGAL_ENTITIES', 'CONTACTS', 'LEADS', 'FACTS'],
  LegalEntity: ['FOUNDATION', 'FINANCIALS', 'BRANDS'],
  MarketSignal: ['FOUNDATION', 'LISTING', 'INTEGRITY'],
  Deal: ['ECONOMICS', 'CONTACT', 'DRAFTING', 'DESIGNS', 'UNIT'],
  PortfolioFit: ['analysis', 'intel', 'leads'],
  Lead: ['analysis', 'intel', 'units'],
  Organisation: ['GENERAL', 'MEMBERS', 'USAGE', 'BILLING', 'CHART', 'MANAGEMENT'],
};

const normalizePortalFocusTab = (
  entityType: EntityType,
  value: string | null | undefined
): string | null => {
  if (typeof value !== 'string') return null;
  const normalizedValue = value.trim();
  if (!normalizedValue) return null;
  const allowedTabs = PORTAL_FOCUS_TAB_MAP[entityType];
  if (!allowedTabs || !allowedTabs.includes(normalizedValue)) return null;
  return normalizedValue;
};

export const parsePortalUrlState = (searchString: string): PortalUrlState => {
  const searchParams = new URLSearchParams(searchString);
  const rawFocus = searchParams.get('fx');
  let focusTarget: PortalUrlState['focusTarget'] = null;

  if (typeof rawFocus === 'string') {
    const separatorIndex = rawFocus.indexOf(':');
    if (separatorIndex > 0 && separatorIndex < rawFocus.length - 1) {
      const rawEntityType = rawFocus.slice(0, separatorIndex).trim();
      const rawEntityId = rawFocus.slice(separatorIndex + 1).trim();
      if (ENTITY_TYPE_SET.has(rawEntityType) && rawEntityId.length > 0) {
        const normalizedEntityType = rawEntityType as EntityType;
        focusTarget = {
          entityType: normalizedEntityType,
          entityId: rawEntityId,
          forceNewSession: searchParams.get('ns') === '1',
          tab: normalizePortalFocusTab(normalizedEntityType, searchParams.get('tab')),
        };
      }
    }
  }

  const rawChatSessionId = searchParams.get('c');
  const normalizedChatSessionId =
    typeof rawChatSessionId === 'string' ? rawChatSessionId.trim() : '';
  const chatSessionId = normalizedChatSessionId.length > 0 ? normalizedChatSessionId : null;

  return { chatSessionId, focusTarget };
};

/**
 * A link that opens one deck at one page with one element selected, in a fresh tab. Read once
 * on load by the URL synchronizer and stripped, the way the Master's worker-tab params are:
 * a reload should land on the deck, not replay the jump.
 */
export interface SlidesDeepLink { projectId: string; pageId: string | null; elementId: string | null }

export const buildSlidesDeepLink = ({ projectId, pageId, elementId }: { projectId: string; pageId?: string | null; elementId?: string | null }): string => {
  const params = new URLSearchParams({ sd: projectId });
  if (pageId) params.set('sp', pageId);
  if (elementId) params.set('se', elementId);
  return `/?${params.toString()}`;
};

export const parseSlidesDeepLink = (searchString: string): SlidesDeepLink | null => {
  const params = new URLSearchParams(searchString);
  const projectId = params.get('sd')?.trim() || '';
  if (!projectId) return null;
  return { projectId, pageId: params.get('sp')?.trim() || null, elementId: params.get('se')?.trim() || null };
};
